import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Chart from 'react-apexcharts';
import { useMemo, useState, type ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { customFormatDate } from '../../../utils/customFormatDate';
import {
  analyticsChartCardSx,
  analyticsColors,
  salesChartBaseOptions,
  salesChartHeaderSx,
  salesChartSubtitleSx,
  salesChartTitleSx,
} from './analyticsStyles';
import {
  formatSparseAxisLabel,
  getDailyAxisLabelFontSize,
  getDetailAxisLabelFontSize,
  getNumberedAxisChartEvents,
  getNumberedAxisLabelFontSize,
  getTrendAxisTitle,
  getTrendChartCategories,
  getTrendTooltipPeriodLabel,
  shouldShowDailyAxisTick,
  shouldUseDailyDateAxisLabels,
  shouldUseNumberedAxisTooltips,
  shouldUseSparseAxisLabels,
  shouldUseBucketRangeLabels,
  shouldShowBucketAxisTick,
  type SalesGranularity,
  type TrendPoint,
} from './salesAnalyticsUtils';

type AxisDateTip = {
  label: string;
  x: number;
  y: number;
};

const NumberedAxisDateTip = ({ tip }: { tip: AxisDateTip | null }) => {
  if (!tip) return null;
  return (
    <Box
      sx={{
        position: 'fixed',
        left: tip.x,
        top: tip.y,
        transform: 'translate(-50%, calc(-100% - 10px))',
        zIndex: 20,
        pointerEvents: 'none',
        bgcolor: '#EEF1F4',
        color: '#334155',
        px: 1.5,
        py: 1,
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: 1.2,
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.1)',
        whiteSpace: 'nowrap',
      }}
    >
      {tip.label}
    </Box>
  );
};

const DETAIL_Y_AXIS_FONT = '13px';
const DETAIL_LEGEND_FONT = '14px';

function withDetailChartStyles(options: any): any {
  const origCustom = options.tooltip?.custom;
  return {
    ...options,
    yaxis: {
      ...options.yaxis,
      labels: {
        ...options.yaxis.labels,
        style: {
          ...(options.yaxis.labels?.style ?? {}),
          fontSize: DETAIL_Y_AXIS_FONT,
        },
      },
    },
    ...(origCustom
      ? {
          tooltip: {
            ...options.tooltip,
            custom: (ctx: { dataPointIndex: number }) => {
              const html = origCustom(ctx) as string;
              return html
                .replace(
                  /font-size:12px;font-weight:600/g,
                  'font-size:14px;font-weight:600'
                )
                .replace(
                  /font-size:12px;color:#1A1C2E/g,
                  'font-size:13px;color:#1A1C2E'
                );
            },
          },
        }
      : {}),
  };
}

function buildTrendXAxisOptions(
  granularity: SalesGranularity,
  categories: string[],
  points: Array<{ date?: string }> | undefined,
  timeDuration?: string,
  periodDays?: number,
  customRangeStart?: Date | null,
  customRangeEnd?: Date | null,
  forceDatedLabels = false
) {
  const pointCount = points?.length ?? categories.length;
  const useBucketRangeLabels =
    forceDatedLabels ||
    shouldUseBucketRangeLabels(granularity, pointCount);
  const useBucketSparse = useBucketRangeLabels && pointCount > 6;
  const useDailyDateLabels =
    !forceDatedLabels &&
    shouldUseDailyDateAxisLabels(
      granularity,
      pointCount,
      timeDuration,
      periodDays
    );
  const useDailySparse = useDailyDateLabels && pointCount > 14;
  const axisTitle = getTrendAxisTitle(
    granularity,
    points as TrendPoint[],
    customRangeStart,
    customRangeEnd
  );
  const isNumberedAxis =
    !forceDatedLabels &&
    !useBucketRangeLabels &&
    !useDailyDateLabels &&
    (granularity === 'weekly' ||
      granularity === 'biweekly' ||
      granularity === 'monthly' ||
      granularity === 'daily');
  const useSparseAxis =
    !forceDatedLabels &&
    !useBucketRangeLabels &&
    !useDailyDateLabels &&
    shouldUseSparseAxisLabels(
      timeDuration,
      granularity,
      pointCount,
      periodDays
    );

  const rotateVal =
    forceDatedLabels || useBucketRangeLabels
      ? pointCount > 3
        ? -45
        : 0
      : useDailyDateLabels && pointCount > 7
        ? -45
        : 0;

  return {
    categories,
    title: {
      text: axisTitle,
      offsetX: 0,
      offsetY: axisTitle ? (rotateVal === -45 ? 10 : 5) : 0,
      style: {
        fontSize: forceDatedLabels ? '14px' : '12px',
        fontWeight: 'bold',
        color: analyticsColors.subtitle,
      },
    },
    labels: {
      rotate: rotateVal,
      rotateAlways: rotateVal !== 0,
      offsetY: 0,
      minHeight: undefined,
      maxHeight: useDailyDateLabels && pointCount > 7 ? 52 : undefined,
      hideOverlappingLabels: false,
      formatter: useDailySparse
        ? (value: string, _timestamp: number, opts?: { dataPointIndex?: number }) => {
            const index = opts?.dataPointIndex ?? 0;
            return shouldShowDailyAxisTick(
              index,
              pointCount,
              points as TrendPoint[]
            )
              ? value
              : '';
          }
        : useBucketSparse
          ? (value: string, _timestamp: number, opts?: { dataPointIndex?: number }) => {
              const index = opts?.dataPointIndex ?? 0;
              return shouldShowBucketAxisTick(index, pointCount) ? value : '';
            }
          : useSparseAxis
            ? (value: string) => formatSparseAxisLabel(value, pointCount)
            : undefined,
      style: {
        colors: analyticsColors.subtitle,
        fontSize: forceDatedLabels
          ? getDetailAxisLabelFontSize(pointCount, granularity)
          : useBucketRangeLabels
            ? pointCount > 8
              ? '8px'
              : '9px'
            : useDailyDateLabels
              ? getDailyAxisLabelFontSize(pointCount)
              : useSparseAxis
                ? '10px'
                : isNumberedAxis
                  ? getNumberedAxisLabelFontSize(pointCount)
                  : '11px',
      },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tooltip: { enabled: false },
  };
}

function getTrendChartBottomPadding(
  granularity: SalesGranularity,
  points: Array<{ date?: string }> | undefined,
  customRangeStart?: Date | null,
  customRangeEnd?: Date | null,
  timeDuration?: string,
  periodDays?: number
): number {
  const pointCount = points?.length ?? 0;
  const axisTitle = getTrendAxisTitle(
    granularity,
    points as TrendPoint[],
    customRangeStart,
    customRangeEnd
  );
  const timelinePadding = axisTitle ? 4 : 0;

  if (shouldUseBucketRangeLabels(granularity, pointCount)) {
    return (pointCount > 3 ? 22 : 6) + timelinePadding;
  }

  if (
    shouldUseDailyDateAxisLabels(
      granularity,
      pointCount,
      timeDuration,
      periodDays
    )
  ) {
    const labelPadding =
      pointCount > 30 ? 16 : pointCount > 14 ? 12 : pointCount > 7 ? 8 : 2;
    return labelPadding + timelinePadding;
  }

  return axisTitle ? 10 : 0;
}

const SalesChartHeader = ({
  title,
  subtitle,
  onViewChart,
}: {
  title: string;
  subtitle: string;
  onViewChart?: () => void;
}) => (
  <Box
    sx={{
      ...salesChartHeaderSx,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 1.5,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={salesChartTitleSx}>{title}</Typography>
      <Typography sx={salesChartSubtitleSx}>{subtitle}</Typography>
    </Box>
    {onViewChart && (
      <Button
        size='small'
        variant='outlined'
        onClick={onViewChart}
        sx={{
          flexShrink: 0,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          borderColor: analyticsColors.cardBorder,
          color: analyticsColors.title,
          px: 1.5,
          py: 0.5,
          borderRadius: '8px',
          '&:hover': {
            borderColor: analyticsColors.chartPrimary,
            bgcolor: 'rgba(188, 142, 105, 0.06)',
          },
        }}
      >
        View Chart
      </Button>
    )}
  </Box>
);

const ChartDetailDialog = ({
  open,
  onClose,
  title,
  subtitle,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  children: ReactNode;
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth='lg'
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '16px',
        overflow: 'hidden',
      },
    }}
  >
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        pr: 1,
        pt: 2.5,
        pb: 1,
      }}
    >
      <Box>
        <Typography sx={{ ...salesChartTitleSx, fontSize: '1.25rem' }}>
          {title}
        </Typography>
        <Typography sx={{ ...salesChartSubtitleSx, fontSize: '0.9375rem' }}>
          {subtitle}
        </Typography>
      </Box>
      <IconButton aria-label='close' onClick={onClose} size='small'>
        <CloseIcon fontSize='small' />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ pt: 1, pb: 3, px: { xs: 1.5, sm: 3 } }}>
      {children}
    </DialogContent>
  </Dialog>
);

function calculateIntegerYAxis(minVal: number, maxVal: number) {
  const range = maxVal - minVal;
  if (range === 0) {
    const min = Math.max(0, Math.floor(minVal) - 2);
    const max = Math.floor(minVal) + 2;
    return { min, max, tickAmount: max - min };
  }

  const targets = [1, 2, 5, 10, 20, 50, 100, 250, 500, 1000, 2500, 5000, 10000];
  let step = 1;
  for (const t of targets) {
    if (range / t <= 5) {
      step = t;
      break;
    }
  }

  const min = Math.floor(minVal / step) * step;
  const max = Math.ceil(maxVal / step) * step;
  const tickAmount = Math.round((max - min) / step);

  return { min, max, tickAmount };
}

export const RevenueTrendChart = ({
  current,
  previous,
  granularity = 'daily',
  showPreviousPeriod = true,
  timeDuration,
  periodDays,
  customRangeStart,
  customRangeEnd,
}: {
  current?: any[];
  previous?: any[];
  granularity?: SalesGranularity;
  showPreviousPeriod?: boolean;
  timeDuration?: string;
  periodDays?: number;
  customRangeStart?: Date | null;
  customRangeEnd?: Date | null;
}) => {
  const categories = getTrendChartCategories(
    current,
    granularity,
    customFormatDate,
    timeDuration,
    periodDays
  );
  const datedCategories = getTrendChartCategories(
    current,
    granularity,
    customFormatDate,
    timeDuration,
    periodDays,
    true
  );
  const pointCount = current?.length ?? 0;
  const [viewChartOpen, setViewChartOpen] = useState(false);

  const activeSeries = showPreviousPeriod
    ? [...(current || []), ...(previous || [])]
    : (current || []);

  const allValues = activeSeries
    .map((i: any) => i.value)
    .filter((v) => v !== undefined && v !== null && !isNaN(v));

  const minVal = allValues.length > 0 ? Math.min(...allValues) : 0;
  const maxVal = allValues.length > 0 ? Math.max(...allValues) : 100;

  const { min: yaxisMin, max: yaxisMax, tickAmount: yaxisTickAmount } = calculateIntegerYAxis(minVal, maxVal);

  const [axisDateTip, setAxisDateTip] = useState<AxisDateTip | null>(null);
  const useNumberedAxisTooltips = shouldUseNumberedAxisTooltips(
    granularity,
    timeDuration,
    periodDays
  );

  const axisHoverHandlers = useMemo(
    () => ({
      onShow: (label: string, anchorX: number, anchorY: number) => {
        setAxisDateTip({ label, x: anchorX, y: anchorY });
      },
      onHide: () => setAxisDateTip(null),
    }),
    []
  );

  const numberedAxisEvents = useMemo(
    () =>
      getNumberedAxisChartEvents(
        current as TrendPoint[],
        categories,
        granularity,
        timeDuration,
        periodDays,
        axisHoverHandlers
      ),
    [current, categories, granularity, timeDuration, periodDays, axisHoverHandlers]
  );

  const options: any = {
    ...salesChartBaseOptions,
    chart: {
      ...salesChartBaseOptions.chart,
      ...(numberedAxisEvents ? { events: numberedAxisEvents } : {}),
    },
    colors: [analyticsColors.chartPrimary, analyticsColors.chartPrimaryMuted],
    stroke: { width: [2.5, 2], dashArray: [0, 6], curve: pointCount <= 2 ? 'straight' : 'smooth' },
    markers: {
      size: pointCount <= 2 ? 6 : pointCount <= 7 ? 5 : 4,
      strokeWidth: 0,
      hover: { size: pointCount <= 2 ? 7 : pointCount <= 7 ? 6 : 5 },
    },
    dataLabels: { enabled: false },
    legend: {
      show: false,
    },
    grid: {
      ...salesChartBaseOptions.grid,
      padding: {
        bottom: getTrendChartBottomPadding(
          granularity,
          current,
          customRangeStart,
          customRangeEnd,
          timeDuration,
          periodDays
        ),
      },
    },
    xaxis: buildTrendXAxisOptions(
      granularity,
      categories,
      current,
      timeDuration,
      periodDays,
      customRangeStart,
      customRangeEnd
    ),
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        const currentPoint = current?.[dataPointIndex];
        const previousPoint = previous?.[dataPointIndex];

        const dateStr = currentPoint?.date;
        const endDateStr = currentPoint?.endDate;

        const category = getTrendTooltipPeriodLabel(
          granularity,
          dataPointIndex,
          categories[dataPointIndex] ?? '',
          dateStr,
          customFormatDate,
          timeDuration,
          endDateStr,
          pointCount
        );

        let bodyHtml = '';

        const currentValue = currentPoint?.value;
        const currentText = currentValue !== undefined ? `QAR ${Math.round(currentValue).toLocaleString()}` : '—';

        bodyHtml += `
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
            <span style="width:18px;height:0;border-top:2.5px solid ${analyticsColors.chartPrimary};display:inline-block;"></span>
            <span>Current Period: <strong>${currentText}</strong></span>
          </div>
        `;

        if (showPreviousPeriod) {
          const previousValue = previousPoint?.value;
          const previousText = previousValue !== undefined ? `QAR ${Math.round(previousValue).toLocaleString()}` : '—';
          bodyHtml += `
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="width:18px;height:0;border-top:2px dashed ${analyticsColors.chartPrimaryMuted};display:inline-block;"></span>
              <span>Previous Period: <strong>${previousText}</strong></span>
            </div>
          `;
        }

        return `
          <div style="padding:0;min-width:160px;border-radius:8px;overflow:hidden;box-shadow:0 4px 12px rgba(15,23,42,0.12);font-family:inherit;">
            <div style="background:#EEF1F4;padding:8px 12px;font-size:12px;font-weight:600;color:#334155;">
              ${category}
            </div>
            <div style="background:#fff;padding:10px 12px;font-size:12px;color:#1A1C2E;line-height:1.6;">
              ${bodyHtml}
            </div>
          </div>
        `;
      },
    },
    yaxis: {
      ...salesChartBaseOptions.yaxis,
      min: yaxisMin,
      max: yaxisMax,
      tickAmount: yaxisTickAmount,
      forceNiceScale: true,
      labels: {
        ...salesChartBaseOptions.yaxis.labels,
        formatter: (v: number) => `QAR ${Math.round(v).toLocaleString()}`,
      },
    },
  };

  const detailOptions: any = withDetailChartStyles({
    ...options,
    chart: {
      ...salesChartBaseOptions.chart,
    },
    grid: {
      ...salesChartBaseOptions.grid,
      padding: {
        bottom: pointCount > 12 ? 48 : 24,
      },
    },
    xaxis: buildTrendXAxisOptions(
      granularity,
      datedCategories,
      current,
      timeDuration,
      periodDays,
      customRangeStart,
      customRangeEnd,
      true
    ),
  });

  const series = showPreviousPeriod
    ? [
        { name: 'Current Period', data: current?.map((i: any) => i.value) || [] },
        { name: 'Previous Period', data: previous?.map((i: any) => i.value) || [] },
      ]
    : [{ name: 'Revenue', data: current?.map((i: any) => i.value) || [] }];

  const revenueSubtitle = showPreviousPeriod
    ? 'Current vs previous period'
    : 'Revenue for selected period';

  return (
    <Card sx={analyticsChartCardSx}>
      <SalesChartHeader
        title='Revenue Trend'
        subtitle={revenueSubtitle}
        onViewChart={() => setViewChartOpen(true)}
      />
      <CardContent
        sx={{ flex: 1, pt: 0.5, pb: 1, px: 1.5, position: 'relative' }}
        onMouseLeave={() => setAxisDateTip(null)}
      >
        <Chart options={options} series={series} type='line' height={280} />
        {useNumberedAxisTooltips && <NumberedAxisDateTip tip={axisDateTip} />}
        {showPreviousPeriod && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2.5,
              mt: 0.5,
              mb: 0.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 18,
                  borderTop: `2.5px solid ${analyticsColors.chartPrimary}`,
                }}
              />
              <Typography
                sx={{ fontSize: '11px', color: analyticsColors.subtitle, lineHeight: 1 }}
              >
                Current Period
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 18,
                  borderTop: `2px dashed ${analyticsColors.chartPrimaryMuted}`,
                }}
              />
              <Typography
                sx={{ fontSize: '11px', color: analyticsColors.subtitle, lineHeight: 1 }}
              >
                Previous Period
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>

      <ChartDetailDialog
        open={viewChartOpen}
        onClose={() => setViewChartOpen(false)}
        title='Revenue Trend'
        subtitle={`${revenueSubtitle} · dates on X-axis`}
      >
        <Chart options={detailOptions} series={series} type='line' height={420} />
        {showPreviousPeriod && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2.5,
              mt: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 18,
                  borderTop: `2.5px solid ${analyticsColors.chartPrimary}`,
                }}
              />
              <Typography
                sx={{ fontSize: DETAIL_LEGEND_FONT, color: analyticsColors.subtitle, lineHeight: 1 }}
              >
                Current Period
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 18,
                  borderTop: `2px dashed ${analyticsColors.chartPrimaryMuted}`,
                }}
              />
              <Typography
                sx={{ fontSize: DETAIL_LEGEND_FONT, color: analyticsColors.subtitle, lineHeight: 1 }}
              >
                Previous Period
              </Typography>
            </Box>
          </Box>
        )}
      </ChartDetailDialog>
    </Card>
  );
};

export const OrdersBarChart = ({
  data,
  granularity = 'daily',
  timeDuration,
  periodDays,
  customRangeStart,
  customRangeEnd,
}: {
  data: any[];
  granularity?: SalesGranularity;
  timeDuration?: string;
  periodDays?: number;
  customRangeStart?: Date | null;
  customRangeEnd?: Date | null;
}) => {
  const categories = getTrendChartCategories(
    data,
    granularity,
    customFormatDate,
    timeDuration,
    periodDays
  );
  const datedCategories = getTrendChartCategories(
    data,
    granularity,
    customFormatDate,
    timeDuration,
    periodDays,
    true
  );
  const revenueByIndex = data?.map((i) => Number(i.revenue ?? i.value ?? 0)) || [];
  const pointCount = data?.length ?? 0;
  const [viewChartOpen, setViewChartOpen] = useState(false);
  const columnWidth =
    pointCount <= 1
      ? '28%'
      : pointCount === 2
        ? '36%'
        : pointCount <= 7
          ? '55%'
          : '42%';

  const allCounts = (data?.map((i) => i.count) || []).filter(
    (v) => v !== undefined && v !== null && !isNaN(v)
  );

  const maxCountVal = allCounts.length > 0 ? Math.max(...allCounts) : 4;

  const { min: yaxisOrdersMin, max: yaxisOrdersMax, tickAmount: yaxisOrdersTickAmount } = calculateIntegerYAxis(0, maxCountVal);

  const [axisDateTip, setAxisDateTip] = useState<AxisDateTip | null>(null);
  const useNumberedAxisTooltips = shouldUseNumberedAxisTooltips(
    granularity,
    timeDuration,
    periodDays
  );

  const axisHoverHandlers = useMemo(
    () => ({
      onShow: (label: string, anchorX: number, anchorY: number) => {
        setAxisDateTip({ label, x: anchorX, y: anchorY });
      },
      onHide: () => setAxisDateTip(null),
    }),
    []
  );

  const numberedAxisEvents = useMemo(
    () =>
      getNumberedAxisChartEvents(
        data as TrendPoint[],
        categories,
        granularity,
        timeDuration,
        periodDays,
        axisHoverHandlers
      ),
    [data, categories, granularity, timeDuration, periodDays, axisHoverHandlers]
  );

  const options: any = {
    ...salesChartBaseOptions,
    chart: {
      ...salesChartBaseOptions.chart,
      ...(numberedAxisEvents ? { events: numberedAxisEvents } : {}),
    },
    colors: [analyticsColors.chartPrimary],
    plotOptions: {
      bar: {
        columnWidth,
        borderRadius: 3,
        borderRadiusApplication: 'end',
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: {
      shared: false,
      intersect: true,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        const category = getTrendTooltipPeriodLabel(
          granularity,
          dataPointIndex,
          categories[dataPointIndex] ?? '',
          data?.[dataPointIndex]?.date,
          customFormatDate,
          timeDuration,
          data?.[dataPointIndex]?.endDate,
          pointCount
        );
        const orders = Number(data?.[dataPointIndex]?.count ?? 0);
        const revenue = revenueByIndex[dataPointIndex] ?? 0;
        const revenueText = `QAR ${Number(revenue).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`;

        return `
          <div style="padding:0;min-width:140px;border-radius:8px;overflow:hidden;box-shadow:0 4px 12px rgba(15,23,42,0.12);font-family:inherit;">
            <div style="background:#EEF1F4;padding:8px 12px;font-size:12px;font-weight:600;color:#334155;">
              ${category}
            </div>
            <div style="background:#fff;padding:10px 12px;font-size:12px;color:#1A1C2E;line-height:1.6;">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
                <span style="width:8px;height:8px;border-radius:50%;background:${analyticsColors.chartPrimary};display:inline-block;"></span>
                <span>Orders: <strong>${orders}</strong></span>
              </div>
              <div style="display:flex;align-items:center;gap:8px;">
                <span style="width:8px;height:8px;border-radius:50%;background:${analyticsColors.chartPrimaryMuted};display:inline-block;"></span>
                <span>Revenue: <strong>${revenueText}</strong></span>
              </div>
            </div>
          </div>
        `;
      },
    },
    grid: {
      ...salesChartBaseOptions.grid,
      padding: {
        bottom: getTrendChartBottomPadding(
          granularity,
          data,
          customRangeStart,
          customRangeEnd,
          timeDuration,
          periodDays
        ),
      },
    },
    xaxis: buildTrendXAxisOptions(
      granularity,
      categories,
      data,
      timeDuration,
      periodDays,
      customRangeStart,
      customRangeEnd
    ),
    yaxis: {
      ...salesChartBaseOptions.yaxis,
      min: yaxisOrdersMin,
      max: yaxisOrdersMax,
      tickAmount: yaxisOrdersTickAmount,
      forceNiceScale: true,
      labels: {
        ...salesChartBaseOptions.yaxis.labels,
        formatter: (v: number) => Math.round(v).toString(),
      },
    },
  };

  const detailOptions: any = withDetailChartStyles({
    ...options,
    chart: {
      ...salesChartBaseOptions.chart,
    },
    grid: {
      ...salesChartBaseOptions.grid,
      padding: {
        bottom: pointCount > 12 ? 48 : 24,
      },
    },
    xaxis: buildTrendXAxisOptions(
      granularity,
      datedCategories,
      data,
      timeDuration,
      periodDays,
      customRangeStart,
      customRangeEnd,
      true
    ),
  });

  const series = [{ name: 'Orders', data: data?.map((i) => i.count) || [] }];

  return (
    <Card sx={analyticsChartCardSx}>
      <SalesChartHeader
        title='Orders'
        subtitle='Orders and revenue per period'
        onViewChart={() => setViewChartOpen(true)}
      />
      <CardContent
        sx={{ flex: 1, pt: 0.5, pb: 1, px: 1.5, position: 'relative' }}
        onMouseLeave={() => setAxisDateTip(null)}
      >
        <Chart options={options} series={series} type='bar' height={280} />
        {useNumberedAxisTooltips && <NumberedAxisDateTip tip={axisDateTip} />}
      </CardContent>

      <ChartDetailDialog
        open={viewChartOpen}
        onClose={() => setViewChartOpen(false)}
        title='Orders'
        subtitle='Orders and revenue per period · dates on X-axis'
      >
        <Chart options={detailOptions} series={series} type='bar' height={420} />
      </ChartDetailDialog>
    </Card>
  );
};

export const ClvBarChart = ({
  data,
  hasData = true,
}: {
  data: any[];
  hasData?: boolean;
}) => {
  const allClvs = (data?.map((i) => i.avg_clv) || []).filter(
    (v) => v !== undefined && v !== null && !isNaN(v)
  );

  const minVal = allClvs.length > 0 ? Math.min(...allClvs) : 0;
  const maxVal = allClvs.length > 0 ? Math.max(...allClvs) : 100;

  const { min: yaxisMin, max: yaxisMax, tickAmount: yaxisTickAmount } = calculateIntegerYAxis(minVal, maxVal);

  const options: any = {
    ...salesChartBaseOptions,
    colors: [analyticsColors.chartPrimary],
    plotOptions: {
      bar: {
        columnWidth: '42%',
        borderRadius: 3,
        borderRadiusApplication: 'end',
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      ...salesChartBaseOptions.xaxis,
      categories: data?.map((i) => i.segment) || [],
    },
    yaxis: {
      ...salesChartBaseOptions.yaxis,
      min: yaxisMin,
      max: yaxisMax,
      tickAmount: yaxisTickAmount,
      forceNiceScale: true,
      labels: {
        ...salesChartBaseOptions.yaxis.labels,
        formatter: (v: number) => `QAR ${Math.round(v).toLocaleString()}`,
      },
    },
  };

  const series = [{ name: 'Avg CLV', data: data?.map((i) => i.avg_clv) || [] }];

  return (
    <Card sx={analyticsChartCardSx}>
      <SalesChartHeader
        title='Customer Lifetime Value'
        subtitle='By order frequency segment for the selected period'
      />
      <CardContent sx={{ flex: 1, pt: 1, pb: 2, px: 1.5 }}>
        {hasData ? (
          <Chart options={options} series={series} type='bar' height={320} />
        ) : (
          <Box
            sx={{
              height: 320,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ color: analyticsColors.subtitle }}>
              No record found
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const StatusDonutChart = ({ breakdown }: any) => {
  const theme = useTheme();
  const labels = ['Delivered', 'Preparing', 'Cancelled'];
  const series = [
    breakdown?.delivered || 0,
    breakdown?.preparing || 0,
    breakdown?.cancelled || 0,
  ];

  const options: any = {
    chart: { background: 'transparent' },
    labels,
    colors: ['#4caf50', theme.palette.primary.main, '#f44336'],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: { show: true, label: 'Total' },
          },
        },
      },
    },
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title='Order Status Breakdown'
        subheader='Distribution of all orders this period'
      />
      <CardContent>
        <Chart options={options} series={series} type='donut' height={300} />
      </CardContent>
    </Card>
  );
};

export { SalesFunnelChart, SalesFunnel } from './SalesFunnelChart';
export type { FunnelStage } from './SalesFunnel';
